/**
 * The five list screens the prototype models as one.
 *
 * Its export holds a `const TS = {…}` keyed by screen, each entry carrying the
 * title, filters, columns and cell kinds. This is the same idea against the real
 * API, so the config is what differs between Providers and Areas — not the page.
 *
 * `fetch` is per screen on purpose. These five endpoints page in four different
 * ways, and pretending otherwise would mean lying about at least one of them:
 *
 *   - facilities  → `Paging.Number` / `Paging.Count`
 *   - providerAdmin → `Page` / `ResultsPerPage` inside `filters`
 *   - areas, facilityRedirects → no paging parameters at all
 *   - providers   → **`Page: 1, Count: 25` hardcoded in `_providers.js`**, which
 *     ignores the page it is handed. The old admin renders a pager over it
 *     anyway; that pager does nothing. Rather than reproduce a control that
 *     lies, the config declares `paged: false` and the page says the list is
 *     capped. Fixing it means changing a shared api module, which is a separate
 *     decision — noted in REWRITE-STATE.md.
 *
 * Cell kinds mirror the prototype's `kinds` array: `id` renders a copyable short
 * GUID, `strong` the row's subject, `bool` a yes/no pill, `date` a tidied
 * timestamp, `badge` a neutral pill, `text` the default.
 */

import { lookupFacility, lookupMembership, lookupProvider, lookupUser } from '../lookups';
import { mappedProductTypes } from '../../enums/ProductTypes';
import { crawlingStatus } from '../../enums/crawlingStatus';
import { CATEGORY_OPTIONS, LANGUAGE_OPTIONS } from '../../components/pages/seoOptions';
import { transactionStatus } from '../../enums/transactionStatus';
import { paymentMethodStatus } from '../../enums/paymentMethodStatus';
import { subscriptionStatus } from '../../enums/subscriptionStatus';

// Column shape: [key, label, kind?, align?]
// Filter shape: { name, label, placeholder?, type?, options?, lookup? }.
// `options` makes it a select, and always comes from the shared enums rather than
// being restated, so a page cannot offer a status the API does not know.
// `lookup` makes it a search-by-name field that stores an id — `(api, key) =>
// Promise<[{label, value}]>`, which is exactly what the api modules' own
// `lookupByNameOrId` returns.
export const TABLE_SCREENS = {
  providers: {
    path: 'providers',
    title: 'Providers',
    label: 'Providers',
    // It pages now, through `searchProvidersPaged`. The old `searchProvider`
    // hardcodes `Page: 1, Count: 25` and throws away the page it is handed,
    // which is why the old admin's pager does nothing — and it is left that way
    // deliberately, because repairing it would revive a dead control on a screen
    // this tree is not supposed to touch. See the comment on it.
    paged: true,
    detail: id => `/next/provider/${id}`,
    create: { label: 'New provider', to: '/next/provider/new' },
    filters: [
      { name: 'providerId', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      { name: 'type', label: 'Type', placeholder: 'Filter by type' },
      { name: 'category', label: 'Category', placeholder: 'Filter by category' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['Name', 'Name', 'strong'],
      ['Category', 'Category'],
      ['Type', 'Type', 'badge'],
      ['CreatedAtCet', 'Created at (CET)', 'date'],
      ['IsPublic', 'Is public', 'bool', 'center']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.providers.searchProvidersPaged({ filters, skipPages: page, resultsCount: perPage })
  },

  providerAdmin: {
    path: 'provider-admin',
    noun: 'provider admin',
    title: 'Provider Admin',
    label: 'Provider Admin',
    paged: true,
    detail: id => `/next/provider-admin/${id}`,
    filters: [
      { name: 'id', label: 'Provider admin id', placeholder: 'Find by id' },
      // Both are free-text on this endpoint — it matches names, not ids — so
      // neither becomes a lookup.
      { name: 'user', label: 'User', placeholder: 'Filter by user' },
      { name: 'providerOrFacility', label: 'Provider or facility', placeholder: 'Filter by name' }
    ],
    columns: [
      ['providerAdminId', 'Provider admin id', 'id'],
      ['userEmail', 'User email', 'strong'],
      ['providerName', 'Provider'],
      ['facilityName', 'Facility']
    ],
    fetch: (api, { filters, page }) =>
      api.providerAdmin.getProviderAdmins({ filters: { ...filters, page } })
  },

  facilities: {
    path: 'facilities',
    noun: 'facility',
    title: 'Facilities',
    label: 'Facilities',
    paged: true,
    detail: id => `/next/facility/${id}`,
    create: { label: 'New facility', to: '/next/facility/new' },
    filters: [
      { name: 'facilityId', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      { name: 'type', label: 'Type', placeholder: 'Filter by type' },
      { name: 'category', label: 'Category', placeholder: 'Filter by category' },
      {
        name: 'providerId',
        label: 'Provider',
        placeholder: 'Name or id',
        lookup: lookupProvider
      }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['Name', 'Name', 'strong'],
      ['Type', 'Type', 'badge'],
      ['Category', 'Category'],
      ['CreatedAt', 'Created at', 'date']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.facilities.searchFacility({ filters, skipPages: page, resultsCount: perPage })
  },

  areas: {
    path: 'areas',
    title: 'Areas',
    label: 'Areas',
    paged: false,
    detail: id => `/next/area/${id}`,
    create: { label: 'New area', to: '/next/area/new' },
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      { name: 'slug', label: 'Slug', placeholder: 'Filter by slug' },
      { name: 'countryCode', label: 'Country', placeholder: 'e.g. DNK' },
      { name: 'zipFrom', label: 'Zip from' },
      { name: 'zipTo', label: 'Zip to' }
    ],
    columns: [
      ['AreaId', 'Id', 'id'],
      ['Name', 'Name', 'strong'],
      ['Slug', 'Slug'],
      ['CountryCode', 'Country'],
      ['IsMainArea', 'Main area', 'bool', 'center'],
      ['IsSuperArea', 'Super area', 'bool', 'center'],
      ['ZipRange', 'Zip range']
    ],
    // Answers `{ areas, pages, totalCount }`.
    rowsKey: 'areas',
    fetch: (api, { filters }) => api.areas.searchAreas({ filters })
  },

  facilityRedirects: {
    path: 'facility-redirects',
    noun: 'redirect',
    title: 'Facility redirects',
    label: 'Facility Redirects',
    paged: false,
    detail: id => `/next/facility-redirect/${id}`,
    create: { label: 'New redirect', to: '/next/facility-redirect/new' },
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'sourceSlug', label: 'Source slug' },
      { name: 'targetSlug', label: 'Target slug' },
      { name: 'targetUrl', label: 'Target URL' },
      { name: 'productSlug', label: 'Product slug' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['SourceSlug', 'Source slug', 'strong'],
      ['TargetSlug', 'Target slug'],
      ['TargetUrl', 'Target URL'],
      ['Type', 'Type', 'badge'],
      ['ProductSlug', 'Product slug'],
      ['IsActive', 'Active', 'bool', 'center'],
      ['CreatedAt', 'Created (UTC)', 'date']
    ],
    fetch: (api, { filters }) => api.facilityRedirects.searchFacilityRedirects({ filters })
  },

  products: {
    path: 'products',
    title: 'Products',
    label: 'Products',
    paged: true,
    // The detail page is 5.000 lines of pricing, schedules and relay config in
    // the old admin and is not ported — see REWRITE-STATE.md. Rows link out to
    // it, which is exactly what this list is for.
    detail: id => `/product/${id}`,
    filters: [
      { name: 'productId', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      { name: 'type', label: 'Type', options: mappedProductTypes },
      { name: 'providerId', label: 'Provider', placeholder: 'Name or id', lookup: lookupProvider },
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility },
      // From the store, like the Signups page — the list of categories is not
      // something to restate in a config.
      { name: 'category', label: 'Category', optionsFrom: 'categories' },
      // Not on the old form, but the endpoint takes it and this list has a Group
      // column — filtering by what you can see is the least surprising addition.
      { name: 'groupName', label: 'Group', placeholder: 'Filter by group' },
      // The heading a product appears under on the public site. Offered as a
      // select rather than a text field because it is an enum of five, and
      // typing "seasonalcourt" correctly is not a skill worth testing.
      // Needs Api.Wannasport#1163; before that ships the filter returns
      // everything, which is what an ignored parameter does.
      {
        name: 'groupHeader',
        label: 'Listed under',
        options: [
          { value: 'BookCourt', label: 'Book court' },
          { value: 'ParticipateActivity', label: 'Participate in activity' },
          { value: 'DropInGolf', label: 'Drop-in golf' },
          { value: 'BookHall', label: 'Book hall' },
          { value: 'SeasonalCourt', label: 'Seasonal court' },
          { value: 'Undefined', label: 'Undefined' }
        ]
      },
      // Only the true case filters today: the endpoint compares against a
      // literal `true` rather than the value it is handed, so asking for the
      // hidden products answers with the visible ones. Offering "No" would
      // promise a filter that silently does the opposite. Api.Wannasport#1163
      // fixes it; when that ships, add `{ value: 'false', label: 'Only hidden' }`
      // here and this comment goes away.
      {
        name: 'isVisible',
        label: 'Visible',
        options: [{ value: 'true', label: 'Only visible' }]
      }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['Name', 'Name', 'strong'],
      ['Type', 'Type', 'badge'],
      ['GroupName', 'Group'],
      // Which heading the product is listed under on the public site. It is
      // editable on the product page and was visible in no list, so a product
      // filed under the wrong heading could only be found by opening products
      // one at a time. Returned by Api.Wannasport#1163; until that ships every
      // row reads `—`, which is what an unanswered field should look like.
      ['GroupHeader', 'Listed under', 'badge'],
      ['FacilityName', 'Facility'],
      ['Category', 'Category'],
      ['Amount', 'Amount', 'money', 'right'],
      ['IsVisible', 'Visible', 'bool', 'center']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.products.searchProduct({ filters, skipPages: page, resultsCount: perPage })
  },

  seoTexts: {
    path: 'seo-texts',
    title: 'SEO texts',
    label: 'SEO Texts',
    subtitle: 'landing page copy — live as soon as it is saved',
    paged: false,
    detail: id => `/next/seo-text/${id}`,
    create: { label: 'New SEO text', to: '/next/seo-text/new' },
    filters: [
      { name: 'areaSlug', label: 'Area slug', placeholder: 'Find by area slug' },
      { name: 'category', label: 'Category', options: CATEGORY_OPTIONS },
      { name: 'language', label: 'Language', options: LANGUAGE_OPTIONS }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['areaSlug', 'Area slug', 'strong'],
      ['categoryName', 'Category'],
      ['languageName', 'Language'],
      ['metaTitle', 'Meta title'],
      // The old list shows a green/grey Yes/No for "is there a body"; a filled
      // pill says the same thing in the same shape as every other boolean here.
      ['extraText', 'Body', 'filled', 'center']
    ],
    fetch: (api, { filters }) => api.seoTexts.searchSeoTexts({ filters })
  },

  facilityTexts: {
    path: 'facility-texts',
    title: 'Facility texts',
    label: 'Facility Texts',
    subtitle: 'per-facility page text and SEO meta, one entry per language',
    paged: false,
    detail: id => `/next/facility-text/${id}`,
    create: { label: 'New facility text', to: '/next/facility-text/new' },
    filters: [
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility },
      { name: 'language', label: 'Language', options: LANGUAGE_OPTIONS }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['facilityName', 'Facility', 'strong'],
      ['languageName', 'Language'],
      ['metaTitle', 'SEO title'],
      ['metaDescription', 'Meta description', 'filled', 'center'],
      ['h1Title', 'H1'],
      ['description', 'Facility text', 'filled', 'center']
    ],
    fetch: (api, { filters }) => api.facilityContent.searchFacilityTexts({ filters })
  },

  activities: {
    path: 'activities',
    noun: 'activity',
    title: 'Activities',
    label: 'Activities',
    subtitle: 'bookable slots, produced by crawlings and schedules',
    paged: true,
    detail: id => `/next/activity/${id}`,
    filters: [
      { name: 'activityId', label: 'Id', placeholder: 'Find by id' },
      { name: 'productId', label: 'Product id', placeholder: 'Filter by product' },
      { name: 'crawlingId', label: 'Crawling id', placeholder: 'Filter by crawling' },
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility },
      { name: 'afterAt', label: 'After', type: 'datetime-local' },
      { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
    ],
    // The payload is camelCase here, unlike most of this admin.
    columns: [
      ['id', 'Id', 'id'],
      ['title', 'Title', 'strong'],
      ['productName', 'Product'],
      ['facilityName', 'Facility'],
      ['startsAt', 'Starts at', 'date'],
      ['endsAt', 'Ends at', 'date'],
      ['isShadowed', 'Shadowed', 'bool', 'center'],
      ['isBuried', 'Buried', 'bool', 'center']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.activities.searchActivities({ filters, skipPages: page, resultsCount: perPage })
  },

  seasonalCourtPurchases: {
    path: 'seasonal-court-purchases',
    noun: 'seasonal court purchase',
    title: 'Seasonal court purchases',
    label: 'Seasonal Court Purchases',
    paged: true,
    detail: id => `/next/seasonal-court-purchase/${id}`,
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'userId', label: 'User', placeholder: 'Email or id', lookup: lookupUser },
      { name: 'purchaseId', label: 'Purchase id' },
      { name: 'subscriptionId', label: 'Subscription id' },
      { name: 'seasonalCourtId', label: 'Seasonal court id' },
      // Free text, as on the old page: `seasonalCourtPurchaseStatus` is an object
      // with a single `Undefined` member, so a select built from it would offer
      // one meaningless option and hide every real status.
      { name: 'status', label: 'Status', placeholder: 'e.g. Active' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['UserName', 'User', 'strong'],
      ['CourtName', 'Court'],
      ['WeekDay', 'Week day', 'weekday'],
      ['Time', 'Time (Copenhagen)', 'time'],
      ['Status', 'Status', 'status'],
      ['TerminatedAt', 'Terminated at', 'date']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.seasonalcourts.searchPurchases({ filters: { ...filters }, skipPages: page, resultsCount: perPage })
  },

  crawlings: {
    path: 'crawlings',
    noun: 'crawling',
    title: 'Crawlings',
    label: 'Crawlings',
    subtitle: 'runs that fetch availability from venue systems',
    paged: true,
    detail: id => `/next/crawling/${id}`,
    create: { label: 'Run a crawling', to: '/next/crawling/run' },
    filters: [
      { name: 'crawlingId', label: 'Id', placeholder: 'Find by id' },
      { name: 'productId', label: 'Product id', placeholder: 'Filter by product' },
      { name: 'status', label: 'Status', options: crawlingStatus },
      { name: 'afterAt', label: 'After', type: 'datetime-local' },
      { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['productName', 'Product', 'strong'],
      ['facilityName', 'Facility'],
      ['systemName', 'System'],
      ['status', 'Status', 'status'],
      ['createdAt', 'Created at', 'date'],
      ['startedAt', 'Started at', 'date'],
      ['durationSeconds', 'Duration (s)']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.crawlings.searchCrawlings({ filters, skipPages: page, resultsCount: perPage })
  },

  resourceGroups: {
    path: 'resource-groups',
    title: 'Resource groups',
    label: 'Resource groups',
    subtitle: 'courts and rooms, split into what can be sold',
    paged: true,
    detail: id => `/next/resource-group/${id}`,
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['name', 'Name', 'strong'],
      ['facilityId', 'Facility id']
    ],
    // Answers `{ resourceGroups, pages }` on some deployments and a bare list on
    // others; `normalise` handles both.
    rowsKey: 'resourceGroups',
    fetch: (api, { filters, page }) =>
      api.resources.searchResourceGroups({ filters: { ...filters, page } })
  },

  nonPartnerFacilities: {
    path: 'non-partner-facilities',
    title: 'Non-partner facilities',
    label: 'Non Partner Facilities',
    subtitle: 'venues we know about but do not sell',
    paged: true,
    detail: id => `/next/non-partner-facility/${id}`,
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      { name: 'slug', label: 'Slug' },
      { name: 'area', label: 'Area' },
      { name: 'sourceType', label: 'Source type' },
      { name: 'reason', label: 'Blacklist reason' },
      // The api module turns this one value into two flags: `active` means not
      // deleted, `deleted` means deleted, `blacklisted` means blacklisted.
      {
        name: 'status',
        label: 'Status',
        options: [
          { value: 'active', label: 'Active' },
          { value: 'deleted', label: 'Deleted' },
          { value: 'blacklisted', label: 'Blacklisted' }
        ]
      }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['name', 'Name', 'strong'],
      ['slug', 'Slug'],
      ['area', 'Area'],
      ['sourceType', 'Source'],
      ['isBlacklisted', 'Blacklisted', 'bool', 'center'],
      ['isDeleted', 'Deleted', 'bool', 'center']
    ],
    rowsKey: 'items',
    fetch: (api, { filters, page, perPage }) =>
      api.nonPartnerFacilities.searchNonPartnerFacilities({
        filters: { ...filters, page, resultsPerPage: perPage }
      })
  },

  bookingAgreements: {
    path: 'booking-agreements',
    title: 'Booking agreements',
    label: 'Booking Agreements',
    subtitle: 'standing instructions to grab the same slot every week',
    paged: true,
    detail: id => `/next/booking-agreement/${id}`,
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'user', label: 'User', placeholder: 'Name, email or id' },
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility },
      { name: 'productId', label: 'Product id' },
      { name: 'status', label: 'Status', placeholder: 'e.g. Active' },
      { name: 'dayOfWeek', label: 'Day of week', placeholder: 'e.g. Monday' },
      { name: 'preferredCourtName', label: 'Preferred court' },
      { name: 'createdFrom', label: 'Created from', type: 'datetime-local' },
      { name: 'createdTo', label: 'Created to', type: 'datetime-local' },
      { name: 'activeFrom', label: 'Active from', type: 'datetime-local' },
      { name: 'activeTo', label: 'Active to', type: 'datetime-local' }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['userName', 'User', 'strong'],
      ['facilityName', 'Facility'],
      ['dayOfWeek', 'Day'],
      ['timeOfDay', 'Time'],
      ['status', 'Status', 'status'],
      ['lastAttemptResult', 'Last result'],
      ['successfulAttemptCount', 'Succeeded', 'text', 'right'],
      ['failedAttemptCount', 'Failed', 'text', 'right']
    ],
    rowsKey: 'items',
    fetch: (api, { filters, page, perPage }) =>
      api.automatedBookingAgreements.searchAutomatedBookingAgreements({
        filters: { ...filters, page, resultsPerPage: perPage }
      })
  },

  lockers: {
    path: 'lockers',
    title: 'Lockers',
    label: 'Lockers',
    subtitle: 'door systems, one per facility',
    // `/admin/locker` answers every locker at once — there is nothing to page.
    paged: false,
    detail: id => `/next/locker/${id}`,
    create: { label: 'New locker', to: '/next/locker/new' },
    filters: [],
    columns: [
      ['id', 'Id', 'id'],
      ['name', 'Name', 'strong'],
      ['facilityName', 'Facility'],
      ['lockerType', 'Type', 'badge']
    ],
    fetch: api => api.lockers.getLockers().then(answer => (answer?.success ? answer.data : [])),
  },

  relayRules: {
    path: 'relay-rules',
    title: 'Relay rules',
    label: 'Relay Rules',
    subtitle: 'how each venue’s confirmation emails are read',
    paged: true,
    detail: id => `/next/relay-rule/${id}`,
    filters: [
      { name: 'id', label: 'Id', placeholder: 'Find by id' },
      { name: 'name', label: 'Name', placeholder: 'Filter by name' },
      {
        name: 'type',
        label: 'Type',
        options: [
          { value: 'BadInfo', label: 'BadInfo' },
          { value: 'Parse', label: 'Parse' },
          { value: 'Irrelevant', label: 'Irrelevant' },
          { value: 'IsSignup', label: 'IsSignup' },
          { value: 'IsCancellation', label: 'IsCancellation' },
          { value: 'Warning', label: 'Warning' }
        ]
      }
    ],
    columns: [
      ['id', 'Id', 'id'],
      ['name', 'Name', 'strong'],
      ['type', 'Type', 'badge'],
      ['order', 'Order', 'text', 'right']
    ],
    rowsKey: 'rules',
    fetch: (api, { filters, page, perPage }) =>
      api.relayRules.searchRules({ filters: { ...filters, page, resultsPerPage: perPage } })
        .then(answer => (answer?.data ?? answer))
  },

  integrations: {
    path: 'integrations',
    title: 'Integrations',
    label: 'Integrations',
    subtitle: 'the venue systems products are booked through',
    paged: true,
    // The old admin has no per-integration page; the list is the section.
    filters: [
      { name: 'integrationId', label: 'Id', placeholder: 'Find by id' },
      { name: 'productId', label: 'Product id', placeholder: 'Filter by product' },
      { name: 'systemType', label: 'System type', optionsFrom: 'systemTypes' },
      { name: 'facilityId', label: 'Facility', placeholder: 'Name or id', lookup: lookupFacility }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['ProductName', 'Product', 'strong'],
      ['FacilityName', 'Facility'],
      ['SystemType', 'System', 'badge'],
      ['CreatedAt', 'Created at', 'date']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.integrations.searchIntegrations({ filters, skipPages: page, resultsCount: perPage })
  },

  playEvents: {
    path: 'play-events',
    title: 'Play events',
    label: 'Play Events',
    subtitle: 'curated PLAY events and their sessions',
    paged: false,
    detail: id => `/next/play-event/${id}`,
    filters: [],
    columns: [
      ['id', 'Id', 'id'],
      ['nameEn', 'Event', 'strong'],
      ['code', 'Code', 'badge'],
      ['sport', 'Sport'],
      ['affiliationName', 'Affiliation'],
      ['sessionCount', 'Sessions', 'text', 'right'],
      ['totalRegistered', 'Signed up', 'text', 'right'],
      ['totalWatchlist', 'Waitlist', 'text', 'right'],
      ['nextSessionStartsAtUtc', 'Next session', 'date']
    ],
    fetch: api => api.playEvents.list({})
  },

  transactions: {
    path: 'transactions',
    title: 'Transactions',
    label: 'Transactions',
    paged: true,
    detail: id => `/next/transaction/${id}`,
    filters: [
      { name: 'transactionId', label: 'Transaction id', placeholder: 'Find by id' },
      { name: 'purchaseId', label: 'Purchase id' },
      { name: 'subscriptionId', label: 'Subscription id' },
      { name: 'paymentMethodId', label: 'Payment method id' },
      { name: 'status', label: 'Status', options: transactionStatus },
      { name: 'afterAt', label: 'After', type: 'datetime-local' },
      { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      // The fifth entry is the sort key the endpoint knows. Only these three:
      // an arrow on a column the query cannot order by is an arrow that lies.
      ['Total', 'Total', 'money', 'right', 'amount'],
      ['Status', 'Status', 'status', undefined, 'status'],
      ['CreatedAtCet', 'Created at (CET)', 'date', undefined, 'created']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.transactions.searchTransaction({ filters, skipPages: page, resultsCount: perPage })
  },

  paymentMethods: {
    path: 'payment-methods',
    noun: 'payment method',
    title: 'Payment methods',
    label: 'Payment Methods',
    paged: true,
    // The kind is in the path, and the list knows it per row.
    detail: (id, row) => `/next/payment-method/${String(row?.Type || 'creditcard').toLowerCase()}/${id}`,
    filters: [
      { name: 'paymentMethodId', label: 'Payment method id', placeholder: 'Find by id' },
      { name: 'userId', label: 'User', placeholder: 'Email or id', lookup: lookupUser },
      { name: 'displayName', label: 'Name' },
      { name: 'status', label: 'Status', options: paymentMethodStatus },
      { name: 'afterAt', label: 'After', type: 'datetime-local' },
      { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      ['Status', 'Status', 'status'],
      ['Name', 'Name', 'strong'],
      ['Token', 'Token'],
      ['Type', 'Type', 'badge'],
      ['CreatedAtCet', 'Created at (CET)', 'date']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.paymentMethods.searchPaymentMethods({ filters, skipPages: page, resultsCount: perPage })
  },

  subscriptions: {
    path: 'subscriptions',
    title: 'Subscriptions',
    label: 'Subscriptions',
    paged: true,
    detail: id => `/next/subscription/${id}`,
    filters: [
      { name: 'subscriptionId', label: 'Subscription id', placeholder: 'Find by id' },
      { name: 'reference', label: 'Reference' },
      { name: 'userId', label: 'User', placeholder: 'Email or id', lookup: lookupUser },
      // Type 2 is a membership.
      { name: 'membershipId', label: 'Membership', placeholder: 'Name or id', lookup: lookupMembership },
      { name: 'status', label: 'Status', options: subscriptionStatus },
      { name: 'afterAt', label: 'After', type: 'datetime-local' },
      { name: 'beforeAt', label: 'Before', type: 'datetime-local' }
    ],
    columns: [
      ['Id', 'Id', 'id'],
      // Two sortable, and only two: the rest of this response is derived or
      // joined, so the query cannot order by it.
      ['Reference', 'Reference', undefined, undefined, 'reference'],
      ['Text', 'Text'],
      ['Status', 'Status', 'status', undefined, 'status'],
      ['CreatedAtCet', 'Created at (CET)', 'date', undefined, 'created']
    ],
    fetch: (api, { filters, page, perPage }) =>
      api.subscriptions.searchSubscription({ filters, skipPages: page, resultsCount: perPage })
  }
};

export const TABLE_SCREEN_LIST = Object.entries(TABLE_SCREENS)
  .map(([key, screen]) => ({ key, ...screen }));
